import type { InputNumberProps } from 'antd';

/** 后端数值最大位数 */
export const BACKEND_NUMBER_DIGIT_LIMIT = 16;

/** 提交时 ×10000（万）额外占用的位数 */
export const TENK_SCALE_DIGITS = 6;

/** 提交时 ×1000000（百万）额外占用的位数 */
export const AMOUNT_SCALE_DIGITS = 8;

/** 无单位换算时的整数位上限 */
export const SAFE_NUMBER_DIGIT_LIMIT = BACKEND_NUMBER_DIGIT_LIMIT;

/** 显示单位为「万」时的整数位上限（提交 ×10000 后仍 ≤ 后端长度） */
export const SAFE_TENK_DIGIT_LIMIT =
  BACKEND_NUMBER_DIGIT_LIMIT - TENK_SCALE_DIGITS;

/** 显示单位为「百万」时的整数位上限（提交 ×1000000 后仍 ≤ 后端长度） */
export const SAFE_AMOUNT_DIGIT_LIMIT =
  BACKEND_NUMBER_DIGIT_LIMIT - AMOUNT_SCALE_DIGITS;

/** 数字输入框默认占位提示 */
export const SAFE_NUMBER_INPUT_PLACEHOLDER = '请输入';

export type SafeNumberUnit = 'default' | 'tenK' | 'amount';

export function getSafeIntegerDigitLimit(unit: SafeNumberUnit = 'default') {
  if (unit === 'tenK') return SAFE_TENK_DIGIT_LIMIT;
  if (unit === 'amount') return SAFE_AMOUNT_DIGIT_LIMIT;
  return SAFE_NUMBER_DIGIT_LIMIT;
}

/**
 * 截断数字输入：整数、小数分别限位。
 * - 整数位数默认与后端对齐；万/百万需按放大倍数提前收紧
 * - 小数不占用整数额度，由 InputNumber 的 precision 等自行约束
 */
export function limitNumberInputDigits(
  value: string | undefined,
  maxIntegerDigits = SAFE_NUMBER_DIGIT_LIMIT,
  maxDecimalDigits?: number,
): string {
  if (value === undefined || value === '') return '';

  const negative = /^-/.test(value);
  const raw = value.replace(/[^\d.]/g, '');
  if (!raw) return negative ? '-' : '';

  const dotIndex = raw.indexOf('.');
  const hasDot = dotIndex >= 0;
  const intRaw = hasDot ? raw.slice(0, dotIndex) : raw;
  const decRaw = hasDot ? raw.slice(dotIndex + 1).replace(/\./g, '') : '';

  const intOut = intRaw.slice(0, maxIntegerDigits);
  const decOut =
    maxDecimalDigits === undefined
      ? decRaw
      : decRaw.slice(0, maxDecimalDigits);

  let result = intOut;
  if (hasDot) {
    result += `.${decOut}`;
  }
  if (negative) {
    result = `-${result}`;
  }
  return result;
}

export function createSafeNumberInputProps(
  maxIntegerDigits = SAFE_NUMBER_DIGIT_LIMIT,
): Pick<InputNumberProps, 'parser' | 'placeholder'> {
  return {
    parser: (displayValue) =>
      limitNumberInputDigits(displayValue, maxIntegerDigits),
    placeholder: SAFE_NUMBER_INPUT_PLACEHOLDER,
  };
}

export function getSafeNumberInputProps(unit: SafeNumberUnit = 'default') {
  return createSafeNumberInputProps(getSafeIntegerDigitLimit(unit));
}

/** 无单位换算 */
export const safeNumberInputProps = getSafeNumberInputProps('default');

/** 显示单位「万」，提交 ×10000 */
export const safeTenKInputProps = getSafeNumberInputProps('tenK');

/** 显示单位「百万」，提交 ×1000000 */
export const safeAmountInputProps = getSafeNumberInputProps('amount');
